<script>
export default {
  name: 'VPreloader',
  props: {
    type: { type: String, default: 'circle' }, // dots | triangle | circle
    loading: { type: Boolean, default: true },
    delay: { type: Number, default: 500 }
  },
  data() {
    return {
      show: false
    };
  },
  computed: {
    hasSlot() {
      return !!this.$slots.default;
    }
  },
  mounted() {
    setTimeout(() => {
      this.show = true;
    }, this.delay);
  }
};
</script>

<template>
  <transition name="fade">
    <div v-if="show" class="v-preloader" :class="`type-${type}`">
      <div v-if="type === 'dots'" class="dots">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
      <div v-if="type === 'triangle'" class="triangle">
        <svg
          viewBox="0 0 30 30"
          xmlns="http://www.w3.org/2000/svg"
          class="slice slice1"
        >
          <polygon points="0 0, 30 30, 0 30" />
        </svg>
        <svg
          viewBox="0 0 30 30"
          xmlns="http://www.w3.org/2000/svg"
          class="slice slice2"
        >
          <polygon points="0 0, 30 0, 30 30" />
        </svg>
      </div>
      <div v-if="type === 'circle'">
        <svg
          viewBox="25 25 50 50"
          xmlns="http://www.w3.org/2000/svg"
          class="circle"
        >
          <circle r="20" cx="50" cy="50" stroke="white" fill="transparent" />
        </svg>
      </div>
      <div v-if="hasSlot" class="text">
        <slot></slot>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.v-preloader {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .triangle {
    display: flex;
    position: relative;
    width: 60px;
    height: 60px;
    align-items: center;
    justify-content: center;
    will-change: transform;

    .slice {
      width: 30px;
      height: 30px;
    }

    .slice1 {
      fill: var(--color-secondary-bg-default);
      animation: triangle-slice1 3s infinite;
    }

    .slice2 {
      fill: var(--color-primary-bg-default);
      animation: triangle-slice2 3s infinite;
    }
  }

  .dots {
    display: inline-block;
    position: relative;
    width: 64px;
    height: 64px;

    .dot {
      position: absolute;
      top: 27px;
      width: 11px;
      height: 11px;
      will-change: transform;
      border-radius: 50%;
      background: var(--color-primary-bg-default);
      animation-timing-function: cubic-bezier(0, 1, 1, 0);

      &:nth-child(1) {
        left: 6px;
        animation: dots-start 0.6s infinite;
      }

      &:nth-child(2) {
        left: 6px;
        animation: dots-mid 0.6s infinite;
      }

      &:nth-child(3) {
        left: 26px;
        animation: dots-mid 0.6s infinite;
      }

      &:nth-child(4) {
        left: 45px;
        animation: dots-end 0.6s infinite;
      }
    }
  }

  .circle {
    width: 75px;
    height: 75px;
    animation: circle-rotate 1.5s linear infinite;
    circle {
      stroke: var(--color-primary-bg-default);
      stroke-width: 4px;
      stroke-dasharray: 150, 200;
      stroke-dashoffset: -10;
      stroke-linecap: round;
      animation: circle-stroke 1.5s linear infinite;
    }

    @keyframes circle-rotate {
      to {
        transform: rotate(360deg);
      }
    }
    @keyframes circle-stroke {
      0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
        stroke: var(--color-secondary-bg-default);
      }
      60% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -35;
        stroke: var(--color-primary-bg-default);
      }
      100% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -124;
        stroke: var(--color-secondary-bg-default);
      }
    }
  }

  .text {
    color: var(--color-text-subtle);
    margin-top: var(--spacing-small);
    font-size: var(--type-small-body);
    font-weight: var(--type-weight-bold);
    text-align: center;
    animation: text-fade 1.5s infinite;
    animation-timing-function: ease-in;
  }
}

@keyframes triangle-slice1 {
  0% {
    transform: translate(calc(50% - 1px), 1px);
  }

  5% {
    transform: translate(calc(50% - 1px), 1px);
  }

  25% {
    transform: translate(calc(50% + 16px), 16px);
  }

  50% {
    transform: translate(calc(50% + 16px), -16px);
  }

  75% {
    transform: translate(calc(50% - 16px), -16px);
  }

  95% {
    transform: translate(calc(50% - 1px), 1px);
  }

  100% {
    transform: translate(calc(50% - 1px), 1px);
  }
}

@keyframes triangle-slice2 {
  0% {
    transform: translate(calc(-50% + 1px), -1px);
  }

  5% {
    transform: translate(calc(-50% + 1px), -1px);
  }

  25% {
    transform: translate(calc(-50% - 16px), -16px);
  }

  50% {
    transform: translate(calc(-50% - 16px), 16px);
  }

  75% {
    transform: translate(calc(-50% + 16px), 16px);
  }

  95% {
    transform: translate(calc(-50% + 1px), -1px);
  }

  100% {
    transform: translate(calc(-50% + 1px), -1px);
  }
}

@keyframes dots-start {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes dots-mid {
  0% {
    transform: translate(0, 0);
  }

  100% {
    transform: translate(19px, 0);
  }
}

@keyframes dots-end {
  0% {
    transform: scale(1);
  }

  100% {
    transform: scale(0);
  }
}

@keyframes text-fade {
  0% {
    opacity: 0.2;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.2;
  }
}
</style>
